// =============================================================================
// Investment Detail Page
// =============================================================================

import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  MapPinIcon,
  DocumentTextIcon,
  BanknotesIcon,
  CalendarIcon,
  ArrowLeftIcon,
  ArrowDownTrayIcon,
  EyeIcon,
  UserCircleIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';
import { Card, DocumentViewerModal, DealImage } from '../../components/ui';
import api from '../../services/api';
import { investorService } from '../../services/investor.service';
import type { InvestmentSummary, Distribution, Document as DocType } from '../../types';

// Contribution type
interface Contribution {
  id: string;
  amount: number;
  fundingDate: string;
  notes?: string;
}

// Waterfall tier type
interface WaterfallTier {
  id: string;
  tierNumber: number;
  name: string;
  hurdleType: 'IRR' | 'EQUITY_MULTIPLE' | 'CASH_ON_CASH';
  hurdleValue?: number;
  lpSplitPercent: number;
  gpSplitPercent: number;
  gpPromotePercent: number;
}

// Share class type
interface ShareClass {
  id: string;
  name: string;
  code: string;
  description?: string;
  priority?: number;
  isDefault?: boolean;
  lpSplitPercent: number;
  gpSplitPercent: number;
  preferredReturnRate?: number;
  preferredReturnType: string;
  compoundingMethod: string;
  isCumulative?: boolean;
  catchUpPercent?: number;
  waterfallTiers: WaterfallTier[];
}

// Investment profile type for this page
interface InvestmentProfile {
  id: string;
  name: string;
  type: string;
  investmentAmount: number;
  ownershipPercentage: number;
  shareClass?: ShareClass | null;
  contributions?: Contribution[];
}

// Extended distribution type with full distribution total
interface InvestmentDistribution extends Distribution {
  distributionTotalAmount: number;
}

// =============================================================================
// Component
// =============================================================================

export function InvestmentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [isLoading, setIsLoading] = useState(true);
  const [investment, setInvestment] = useState<InvestmentSummary | null>(null);
  const [investorProfiles, setInvestorProfiles] = useState<InvestmentProfile[]>([]);
  const [distributions, setDistributions] = useState<InvestmentDistribution[]>([]);
  const [documents, setDocuments] = useState<DocType[]>([]);
  const [shareClasses, setShareClasses] = useState<ShareClass[]>([]);
  const [viewingDocument, setViewingDocument] = useState<DocType | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  useEffect(() => {
    loadInvestmentDetails();
  }, [id]);

  const loadInvestmentDetails = async () => {
    setIsLoading(true);
    try {
      const response = await api.get<{
        success: boolean;
        data: {
          deal: InvestmentSummary['deal'];
          investorProfile: { id: string; name: string; type: string };
          investorProfiles: Array<{
            id: string;
            name: string;
            type: string;
            investmentAmount: number;
            ownershipPercentage: number;
            shareClass?: ShareClass | null;
            contributions?: Array<{
              id: string;
              amount: number;
              fundingDate: string;
              notes?: string;
            }>;
          }>;
          investmentAmount: number;
          ownershipPercentage: number;
          totalDistributed: number;
          shareClasses?: ShareClass[];
          allocations: Array<{
            id: string;
            amount: number;
            distribution: {
              id: string;
              type: string;
              distributionDate: string;
              totalAmount: number;
              notes: string | null;
              paymentMethod: string;
            };
          }>;
          documents: Array<{
            id: string;
            type: string;
            name: string;
            description: string | null;
            createdAt: string;
          }>;
        };
      }>(`/investor/investments/${id}`);

      const data = response.data.data;

      // Store all investor profiles
      setInvestorProfiles(data.investorProfiles || []);

      // Store share classes
      setShareClasses(data.shareClasses || []);

      // Map API response to component state
      setInvestment({
        deal: data.deal,
        profile: {
          id: data.investorProfile.id,
          userId: '',
          type: data.investorProfile.type as any,
          entityName: data.investorProfile.name,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        },
        investmentAmount: data.investmentAmount,
        ownershipPercentage: data.ownershipPercentage,
        totalDistributed: data.totalDistributed,
      });

      // Map allocations to distributions format
      setDistributions(
        data.allocations.map((a) => ({
          id: a.distribution.id,
          dealId: data.deal.id,
          deal: data.deal,
          totalAmount: a.amount, // Investor's allocation amount
          distributionTotalAmount: a.distribution.totalAmount, // Full distribution total
          distributionDate: a.distribution.distributionDate,
          type: a.distribution.type as Distribution['type'],
          paymentMethod: a.distribution.paymentMethod as Distribution['paymentMethod'],
          notes: a.distribution.notes || undefined,
        }))
      );

      setDocuments(
        data.documents.map((d) => ({
          id: d.id,
          dealId: data.deal.id,
          deal: data.deal,
          type: d.type as DocType['type'],
          name: d.name,
          description: d.description || undefined,
          createdAt: d.createdAt,
        }))
      );
    } catch (error) {
      console.error('Failed to load investment details:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = async (doc: DocType) => {
    setDownloadingId(doc.id);
    try {
      const response = await api.get<{ success: boolean; data: { url: string } }>(
        `/investor/documents/${doc.id}/download`
      );
      if (response.data.success && response.data.data.url) {
        window.open(response.data.data.url, '_blank');
      }
    } catch (error) {
      console.error('Failed to download document:', error);
    } finally {
      setDownloadingId(null);
    }
  };

  const handleView = (doc: DocType) => {
    setViewingDocument(doc);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC',
    });
  };

  const getPropertyTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      MULTIFAMILY: 'Multifamily',
      RETAIL: 'Retail',
      OFFICE: 'Office',
      INDUSTRIAL: 'Industrial',
      MIXED_USE: 'Mixed Use',
      HOTEL: 'Hotel',
      SELF_STORAGE: 'Self Storage',
      OTHER: 'Other',
    };
    return labels[type] || type;
  };

  const getDocumentTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      QUARTERLY_REPORT: 'Quarterly Report',
      K1: 'K-1 Tax Document',
      PPM: 'PPM',
      SUBSCRIPTION_AGREEMENT: 'Subscription Agreement',
      FINANCIAL_STATEMENT: 'Financial Statement',
      OPERATING_AGREEMENT: 'Operating Agreement',
      OTHER: 'Other',
    };
    return labels[type] || type;
  };

  const getDistributionTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      CASH_FLOW: 'Cash Flow',
      REFINANCE: 'Refinance',
      SALE: 'Sale Proceeds',
      RETURN_OF_CAPITAL: 'Return of Capital',
      OTHER: 'Other',
    };
    return labels[type] || type;
  };

  // Calculate initial equity vs additional capital calls for this investor's view
  const calculateEquityBreakdown = () => {
    let initialEquity = 0;
    let additionalCapitalCalls = 0;

    for (const profile of investorProfiles) {
      if (profile.contributions && profile.contributions.length > 0) {
        // Sort contributions by funding date
        const sortedContributions = [...profile.contributions].sort(
          (a, b) => new Date(a.fundingDate).getTime() - new Date(b.fundingDate).getTime()
        );
        // First contribution is initial, rest are additional
        initialEquity += sortedContributions[0].amount;
        for (let i = 1; i < sortedContributions.length; i++) {
          additionalCapitalCalls += sortedContributions[i].amount;
        }
      } else {
        // No contributions tracked, treat entire amount as initial
        initialEquity += profile.investmentAmount;
      }
    }

    return { initialEquity, additionalCapitalCalls };
  };

  const equityBreakdown = calculateEquityBreakdown();

  const getProfileTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      INDIVIDUAL: 'Individual',
      LLC: 'LLC',
      TRUST: 'Trust',
      CORPORATION: 'Corporation',
      PARTNERSHIP: 'Partnership',
      IRA: 'IRA',
    };
    return labels[type] || type;
  };

  // Get the share class used by the investor (if any)
  const investorShareClass = investorProfiles.find(p => p.shareClass)?.shareClass;

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 w-64 bg-gray-200 rounded" />
        <div className="h-48 bg-gray-200 rounded-xl" />
        <div className="grid grid-cols-2 gap-6">
          <div className="h-64 bg-gray-200 rounded-xl" />
          <div className="h-64 bg-gray-200 rounded-xl" />
        </div>
      </div>
    );
  }

  if (!investment) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Investment not found</p>
        <Link to="/investments" className="text-primary-600 hover:text-primary-500 mt-2 inline-block">
          Back to Investments
        </Link>
      </div>
    );
  }

  // Map distributions - totalAmount already contains the investor's pro-rata share (allocation amount)
  const investorDistributions = distributions.map((d) => ({
    ...d,
    investorAmount: d.totalAmount, // Already the investor's allocation amount
  }));

  return (
    <div className="space-y-8">
      {/* Back Link */}
      <Link
        to="/investments"
        className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700"
      >
        <ArrowLeftIcon className="h-4 w-4" />
        Back to Investments
      </Link>

      {/* Overview: Deal Info (left) | Map (right) */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Left Column: Header + Stats + Deal Info */}
        <div className="lg:col-span-3 space-y-4">
          {/* Property Header */}
          <Card padding="lg">
            <div className="flex items-start gap-4">
              <div className="w-24 h-24 rounded-xl overflow-hidden flex-shrink-0">
                <DealImage
                  dealId={investment.deal.id}
                  dealName={investment.deal.name}
                  imageUrl={investment.deal.imageUrl}
                  className="w-full h-full object-cover"
                  iconClassName="h-10 w-10 text-primary-600"
                  fallbackClassName="w-full h-full flex items-center justify-center bg-primary-100"
                />
              </div>
              <div>
                <div className="flex items-center gap-3 flex-wrap">
                  <h1 className="text-2xl font-bold text-gray-900">{investment.deal.name}</h1>
                  <span className="px-2.5 py-1 text-xs font-medium bg-primary-100 text-primary-800 rounded-full">
                    {getPropertyTypeLabel(investment.deal.propertyType)}
                  </span>
                </div>
                <div className="flex items-center gap-1 text-gray-500 mt-2">
                  <MapPinIcon className="h-5 w-5" />
                  {investment.deal.address}, {investment.deal.city}, {investment.deal.state}{' '}
                  {investment.deal.zipCode}
                </div>
                {investment.deal.acquisitionDate && (
                  <div className="flex items-center gap-1 text-sm text-gray-400 mt-1">
                    <CalendarIcon className="h-4 w-4" />
                    Acquired: {formatDate(investment.deal.acquisitionDate)}
                  </div>
                )}
              </div>
            </div>
          </Card>

          {/* Stats Cards - 2x2 Grid */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-sm text-gray-500">Your Investment</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(investment.investmentAmount)}</p>
              {equityBreakdown.additionalCapitalCalls > 0 && (
                <div className="mt-2 pt-2 border-t border-gray-100 space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-500">Initial Funding</span>
                    <span className="text-gray-700">{formatCurrency(equityBreakdown.initialEquity)}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-500">Additional Contributions</span>
                    <span className="text-gray-700">{formatCurrency(equityBreakdown.additionalCapitalCalls)}</span>
                  </div>
                </div>
              )}
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-sm text-gray-500">Ownership</p>
              <p className="text-2xl font-bold text-gray-900">{investment.ownershipPercentage.toFixed(2)}%</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-sm text-gray-500">Total Distributed</p>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(investment.totalDistributed)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-sm text-gray-500">Total Equity Raised</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(investment.deal.totalEquityRaised)}</p>
            </div>
          </div>
        </div>

        {/* Right Column: Google Map */}
        <div className="lg:col-span-2">
          {investment.deal.address && investment.deal.city && investment.deal.state ? (
            <div className="rounded-lg overflow-hidden h-full border border-gray-200">
              <iframe
                src={`https://www.google.com/maps?q=${encodeURIComponent(`${investment.deal.address}, ${investment.deal.city}, ${investment.deal.state} ${investment.deal.zipCode}`)}&z=15&output=embed&iwloc=near`}
                className="w-full h-full border-0"
                style={{ minHeight: '300px' }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Property Location"
              />
            </div>
          ) : (
            <div className="rounded-lg border border-gray-200 flex flex-col items-center justify-center h-full">
              <MapPinIcon className="h-10 w-10 text-gray-300 mb-2" />
              <p className="text-gray-400 text-sm">No address set</p>
            </div>
          )}
        </div>
      </div>

      {/* Deal Information - Full Width Below */}
      <Card padding="md">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Deal Information</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <p className="text-sm text-gray-500">Property Type</p>
            <p className="font-medium">{getPropertyTypeLabel(investment.deal.propertyType)}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Acquisition Date</p>
            <p className="font-medium">{investment.deal.acquisitionDate ? formatDate(investment.deal.acquisitionDate) : 'Not set'}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Location</p>
            <p className="font-medium">{investment.deal.city}, {investment.deal.state}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Status</p>
            <p className="font-medium text-green-600">{investment.deal.isActive ? 'Active' : 'Inactive'}</p>
          </div>
        </div>

        {/* Description */}
        {investment.deal.description && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <p className="text-sm text-gray-500 mb-1">Description</p>
            <p className="text-sm text-gray-600">{investment.deal.description}</p>
          </div>
        )}

        {/* Custom Deal Information */}
        {(() => {
          const fields = (investment.deal as any)?.customFields;
          if (!fields || !Array.isArray(fields) || fields.length === 0) return null;
          return (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="grid grid-cols-2 gap-x-8 gap-y-2">
                {fields.map((f: { key: string; value: string }, i: number) => (
                  <div key={i} className="flex justify-between text-sm py-1">
                    <span className="text-gray-500">{f.key}</span>
                    <span className="font-medium text-gray-900">{f.value}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}
      </Card>

      {/* Your Investment Details Card */}
      {investorProfiles.length > 0 && (
        <Card padding="md">
          <div className="flex items-center gap-2 mb-4">
            <BanknotesIcon className="h-5 w-5 text-primary-600" />
            <h2 className="text-lg font-semibold text-gray-900">Your Investment Details</h2>
          </div>

          {investorProfiles.map((profile) => (
            <div key={profile.id} className="space-y-4">
              {/* Profile Info */}
              <Link
                to="/profile"
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100 hover:bg-gray-100 transition-colors group"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary-100 rounded-lg group-hover:bg-primary-200 transition-colors">
                    <UserCircleIcon className="h-5 w-5 text-primary-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 group-hover:text-primary-600 transition-colors">{profile.name}</p>
                    <p className="text-sm text-gray-500">{getProfileTypeLabel(profile.type)}</p>
                  </div>
                </div>
                <span className="text-xs text-gray-400">View Profile →</span>
              </Link>

              {/* Investment Summary */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-primary-50 rounded-lg p-4 border border-primary-100">
                  <p className="text-sm text-primary-600 font-medium">Total Invested</p>
                  <p className="text-2xl font-bold text-primary-900">{formatCurrency(profile.investmentAmount)}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <p className="text-sm text-gray-600 font-medium">Ownership Stake</p>
                  <p className="text-2xl font-bold text-gray-900">{profile.ownershipPercentage.toFixed(2)}%</p>
                </div>
              </div>

              {/* Funding History */}
              {profile.contributions && profile.contributions.length > 0 && (
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                  <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                    <h3 className="text-sm font-semibold text-gray-700">
                      Funding History
                    </h3>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {profile.contributions.length} contribution{profile.contributions.length !== 1 ? 's' : ''} to this investment
                    </p>
                  </div>
                  <div className="divide-y divide-gray-100">
                    {profile.contributions.map((contrib, index) => (
                      <div key={contrib.id} className="flex items-center justify-between px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${
                            index === 0
                              ? 'bg-primary-100 text-primary-700'
                              : 'bg-amber-100 text-amber-700'
                          }`}>
                            {index + 1}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {index === 0 ? 'Initial Funding' : 'Additional Contribution'}
                            </p>
                            <p className="text-xs text-gray-500">{formatDate(contrib.fundingDate)}</p>
                            {contrib.notes && (
                              <p className="text-xs text-gray-400 mt-0.5">{contrib.notes}</p>
                            )}
                          </div>
                        </div>
                        <p className="text-sm font-semibold text-gray-900">{formatCurrency(contrib.amount)}</p>
                      </div>
                    ))}
                  </div>
                  {/* Summary row */}
                  {profile.contributions.length > 1 && (
                    <div className="bg-gray-50 px-4 py-3 border-t border-gray-200 flex justify-between">
                      <span className="text-sm font-medium text-gray-600">Total</span>
                      <span className="text-sm font-bold text-gray-900">{formatCurrency(profile.investmentAmount)}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </Card>
      )}

      {/* Deal Terms - Only show if any deal terms are set */}
      {investment?.deal && ((investment.deal as any).investmentPeriodStart || (investment.deal as any).investmentPeriodEnd) && (
        <Card padding="md">
          <div className="flex items-center gap-2 mb-4">
            <CalendarIcon className="h-5 w-5 text-primary-600" />
            <h2 className="text-lg font-semibold text-gray-900">Deal Terms</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500 font-medium mb-1">Investment Period Start</p>
              <p className="text-sm font-medium text-gray-900">
                {(investment.deal as any).investmentPeriodStart
                  ? formatDate((investment.deal as any).investmentPeriodStart)
                  : 'Not set'}
              </p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500 font-medium mb-1">Investment Period End</p>
              <p className="text-sm font-medium text-gray-900">
                {(investment.deal as any).investmentPeriodEnd
                  ? formatDate((investment.deal as any).investmentPeriodEnd)
                  : 'Not set'}
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Share Class / Waterfall Structure */}
      {(investorShareClass || shareClasses.length > 0) && (
        <Card padding="md">
          <div className="flex items-center gap-2 mb-4">
            <ChartBarIcon className="h-5 w-5 text-primary-600" />
            <h2 className="text-lg font-semibold text-gray-900">Share Class & Waterfall</h2>
          </div>

          {/* Your Share Class */}
          {investorShareClass && (
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Your Share Class</h3>
              <div className="p-4 bg-primary-50 rounded-lg border border-primary-100">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="font-semibold text-primary-900">{investorShareClass.name}</p>
                    <p className="text-sm text-primary-700">Code: {investorShareClass.code}</p>
                  </div>
                  {investorShareClass.preferredReturnRate && (
                    <div className="text-right">
                      <p className="text-2xl font-bold text-primary-900">
                        {(investorShareClass.preferredReturnRate * 100).toFixed(1)}%
                      </p>
                      <p className="text-xs text-primary-600">Preferred Return</p>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
                  <div className="bg-white rounded p-2">
                    <p className="text-xs text-gray-500">LP Split</p>
                    <p className="font-semibold text-gray-900">
                      {(investorShareClass.lpSplitPercent * 100).toFixed(2)}%
                    </p>
                  </div>
                  <div className="bg-white rounded p-2">
                    <p className="text-xs text-gray-500">GP Split</p>
                    <p className="font-semibold text-gray-900">
                      {(investorShareClass.gpSplitPercent * 100).toFixed(2)}%
                    </p>
                  </div>
                  <div className="bg-white rounded p-2">
                    <p className="text-xs text-gray-500">Compounding</p>
                    <p className="font-semibold text-gray-900 capitalize">
                      {investorShareClass.compoundingMethod.toLowerCase()}
                    </p>
                  </div>
                  <div className="bg-white rounded p-2">
                    <p className="text-xs text-gray-500">Pref Type</p>
                    <p className="font-semibold text-gray-900">
                      {investorShareClass.preferredReturnType === 'TRUE_PREFERRED' ? 'True Pref' : 'Pari Passu'}
                    </p>
                  </div>
                </div>

                {/* Waterfall Tiers */}
                {investorShareClass.waterfallTiers && investorShareClass.waterfallTiers.length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-xs font-semibold text-gray-600 uppercase mb-2">Waterfall Tiers</h4>
                    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden inline-block">
                      <table className="divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 min-w-[100px]">Tier</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 min-w-[100px]">Hurdle</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 min-w-[100px]">GP Promote</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {investorShareClass.waterfallTiers.map((tier) => (
                            <tr key={tier.id}>
                              <td className="px-4 py-2 text-sm font-medium text-gray-900">{tier.name}</td>
                              <td className="px-4 py-2 text-sm text-gray-600">
                                {tier.hurdleValue != null
                                  ? tier.hurdleType === 'EQUITY_MULTIPLE'
                                    ? `${tier.hurdleValue.toFixed(2)}x`
                                    : tier.hurdleType === 'CASH_ON_CASH'
                                      ? `${(tier.hurdleValue * 100).toFixed(1)}% CoC`
                                      : `${(tier.hurdleValue * 100).toFixed(1)}% IRR`
                                  : '-'}
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-900">
                                {tier.gpPromotePercent ? `${(tier.gpPromotePercent * 100).toFixed(2)}%` : '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* All Deal Share Classes (if no investor share class or for reference) */}
          {!investorShareClass && shareClasses.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Deal Share Classes</h3>
              <div className="space-y-4">
                {shareClasses.map((sc) => (
                  <div key={sc.id} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-gray-900">{sc.name}</p>
                        <span className="px-2 py-0.5 text-xs bg-gray-200 text-gray-700 rounded">{sc.code}</span>
                        {sc.isDefault && (
                          <span className="px-2 py-0.5 text-xs bg-green-100 text-green-800 rounded">Default</span>
                        )}
                      </div>
                      {sc.preferredReturnRate != null && sc.preferredReturnRate > 0 && (
                        <div className="text-right">
                          <p className="text-xl font-bold text-gray-900">
                            {(sc.preferredReturnRate * 100).toFixed(1)}%
                          </p>
                          <p className="text-xs text-gray-500">Preferred Return</p>
                        </div>
                      )}
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                      <div className="bg-white rounded p-2">
                        <p className="text-xs text-gray-500">LP Split</p>
                        <p className="font-semibold text-gray-900">
                          {(sc.lpSplitPercent * 100).toFixed(2)}%
                        </p>
                      </div>
                      <div className="bg-white rounded p-2">
                        <p className="text-xs text-gray-500">GP Split</p>
                        <p className="font-semibold text-gray-900">
                          {(sc.gpSplitPercent * 100).toFixed(2)}%
                        </p>
                      </div>
                      <div className="bg-white rounded p-2">
                        <p className="text-xs text-gray-500">Compounding</p>
                        <p className="font-semibold text-gray-900 capitalize">
                          {sc.compoundingMethod.toLowerCase()}
                        </p>
                      </div>
                      <div className="bg-white rounded p-2">
                        <p className="text-xs text-gray-500">Pref Type</p>
                        <p className="font-semibold text-gray-900">
                          {sc.preferredReturnType === 'TRUE_PREFERRED' ? 'True Pref' : 'Pari Passu'}
                        </p>
                      </div>
                    </div>

                    {/* Waterfall Tiers for this share class */}
                    {sc.waterfallTiers && sc.waterfallTiers.length > 0 && (
                      <div>
                        <h4 className="text-xs font-semibold text-gray-600 uppercase mb-2">Waterfall Tiers</h4>
                        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden inline-block">
                          <table className="divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 min-w-[100px]">Tier</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 min-w-[100px]">Hurdle</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 min-w-[100px]">GP Promote</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {sc.waterfallTiers.map((tier) => (
                                <tr key={tier.id}>
                                  <td className="px-4 py-2 text-sm font-medium text-gray-900">{tier.name}</td>
                                  <td className="px-4 py-2 text-sm text-gray-600">
                                    {tier.hurdleValue != null
                                      ? tier.hurdleType === 'IRR'
                                        ? `${(tier.hurdleValue * 100).toFixed(1)}% IRR`
                                        : `${tier.hurdleValue.toFixed(2)}x`
                                      : '-'}
                                  </td>
                                  <td className="px-4 py-2 text-sm text-gray-900">
                                    {tier.gpPromotePercent ? `${(tier.gpPromotePercent * 100).toFixed(2)}%` : '-'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {!investorShareClass && shareClasses.length === 0 && (
            <p className="text-gray-500 text-sm text-center py-4">
              No share classes configured for this deal
            </p>
          )}
        </Card>
      )}

      {/* Distribution & Documents Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Distributions */}
        <Card padding="md">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Distributions</h2>
            <Link to="/distributions" className="text-sm text-primary-600 hover:text-primary-500">
              View all
            </Link>
          </div>

          {investorDistributions.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <BanknotesIcon className="h-8 w-8 mx-auto text-gray-400" />
              <p className="mt-2 text-sm">No distributions yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {investorDistributions.slice(0, 5).map((dist) => (
                <div
                  key={dist.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div>
                    <p className="text-sm font-medium text-gray-900">{dist.notes}</p>
                    <p className="text-xs text-gray-500">
                      {formatDate(dist.distributionDate)} -{' '}
                      {getDistributionTypeLabel(dist.type)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-green-600">
                      {formatCurrency(dist.investorAmount)}
                    </p>
                    <p className="text-xs text-gray-500">
                      {investment.ownershipPercentage.toFixed(2)}% of {formatCurrency(dist.distributionTotalAmount)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Documents */}
        <Card padding="md">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Documents</h2>
            <Link to="/documents" className="text-sm text-primary-600 hover:text-primary-500">
              View all
            </Link>
          </div>

          {documents.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <DocumentTextIcon className="h-8 w-8 mx-auto text-gray-400" />
              <p className="mt-2 text-sm">No documents available</p>
            </div>
          ) : (
            <div className="space-y-2">
              {documents.map((doc) => (
                <div
                  key={doc.id}
                  className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg transition-colors group"
                >
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-gray-100 rounded-lg">
                      <DocumentTextIcon className="h-4 w-4 text-gray-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{doc.name}</p>
                      <p className="text-xs text-gray-500">
                        {getDocumentTypeLabel(doc.type)} - {formatDate(doc.createdAt)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => handleView(doc)}
                      className="p-2 text-gray-400 hover:text-primary-600"
                      title="View"
                    >
                      <EyeIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleDownload(doc)}
                      className="p-2 text-gray-400 hover:text-primary-600"
                      title="Download"
                      disabled={downloadingId === doc.id}
                    >
                      <ArrowDownTrayIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Document Viewer Modal */}
      {viewingDocument && (
        <DocumentViewerModal
          isOpen={!!viewingDocument}
          onClose={() => setViewingDocument(null)}
          documentId={viewingDocument.id}
          fileName={viewingDocument.name}
          getEmbedUrls={investorService.getDocumentViewUrl}
        />
      )}
    </div>
  );
}

export default InvestmentDetailPage;
